import { TWebsiteBuilderNode } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderNode";
import { applyAddNodeTreeChange, getAddNodeInverseAction, IAddNodeAction } from "./actions/node/addNodeAction";
import {
  applyDeleteNodeTreeChange,
  getDeleteNodeInverseAction,
  IDeleteNodeAction,
} from "./actions/node/deleteNodeAction";
import {
  applyMoveElementTreeChange,
  getMoveElementInverseAction,
  IMoveElementAction,
} from "./actions/element/moveElementAction";
import { ISelectNodeAction } from "./actions/node/selectNodeAction";
import { IUpdateSelectedNodeAction } from "./actions/node/updateSelectedNode";
import { IWebsiteBuilderRoot } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderRoot";
import {
  applyResizeElementTreeChange,
  getResizeElementInverseAction,
  IResizeElementAction,
} from "./actions/element/resizeElementAction";
import {
  applyUpdateElementSettingsTreeChange,
  getUpdateElementSettingsInverseAction,
  IUpdateElementSettingsAction,
} from "./actions/element/updateElementSettings";
import {
  applyMoveSectionTreeChange,
  getMoveSectionInverseAction,
  IMoveSectionAction,
} from "./actions/section/moveSection";
import {
  applyResizeSectionTreeChange,
  getResizeSectionInverseAction,
  IResizeSectionAction,
} from "./actions/section/resizeSection";
import {
  applyBringToFrontElementTreeChange,
  getBringToFrontElementInverseAction,
  IBringToFrontElementAction,
} from "./actions/element/bringToFrontElement";
import {
  applyBringToBackElementTreeChange,
  getBringToBackElementInverseAction,
  IBringToBackElementAction,
} from "./actions/element/bringToBackElement";
import {
  applyUpdateElementStretchTreeChange,
  getUpdateElementStretchInverseAction,
  IUpdateElementStretchAction,
} from "./actions/element/updateElementStretch";
import { ILoadThemesAction } from "./actions/themes/loadThemesAction";
import { ISelectThemeAction } from "./actions/themes/selectThemeAction";
import { ILoadGlobalStylesAction } from "./actions/globalStyles/loadGlobalStyles";
import { IUpdateGlobalStylesAction } from "./actions/globalStyles/updateGlobalStyles";
import { ISetDesignModeAction } from "./actions/mode/setDesignMode";
import { ISetPreviewModeAction } from "./actions/mode/setPreviewMode";

export const WebsiteBuilderActionType = {
  EXIT_EDITOR: "websiteBuilder.EXIT_EDITOR",
  SET_DESIGN_MODE: "websiteBuilder.SET_DESIGN_MODE",
  SET_PREVIEW_MODE: "websiteBuilder.SET_PREVIEW_MODE",
  LOAD_THEMES: "websiteBuilder.LOAD_THEMES",
  SELECT_THEME: "websiteBuilder.SELECT_THEME",
  LOAD_GLOBAL_STYLES: "websiteBuilder.LOAD_SCOPED_STYLES",
  UPDATE_GLOBAL_STYLES: "websiteBuilder.UPDATE_SCOPED_STYLES",
  SELECT_NODE: "websiteBuilder.SELECT_NODE",
  UPDATE_SELECTED_NODE: "websiteBuilder.UPDATE_SELECTED_NODE",
  UNDO: "websiteBuilder.UNDO",
  REDO: "websiteBuilder.REDO",
  ADD_NODE: "websiteBuilder.ADD_NODE",
  DELETE_NODE: "websiteBuilder.DELETE_NODE",
  MOVE_ELEMENT: "websiteBuilder.MOVE_ELEMENT",
  RESIZE_ELEMENT: "websiteBuilder.RESIZE_ELEMENT",
  BRING_TO_FRONT_ELEMENT: "websiteBuilder.BRING_TO_FRONT_ELEMENT",
  BRING_TO_BACK_ELEMENT: "websiteBuilder.BRING_TO_BACK_ELEMENT",
  UPDATE_ELEMENT_SETTINGS: "websiteBuilder.UPDATE_ELEMENT_SETTINGS",
  UPDATE_ELEMENT_STRETCH: "websiteBuilder.UPDATE_ELEMENT_STRETCH",
  MOVE_SECTION: "websiteBuilder.MOVE_SECTION",
  RESIZE_SECTION: "websiteBuilder.RESIZE_SECTION",
} as const;

export interface INodeSnapshot {
  parentId: string;
  node: TWebsiteBuilderNode;
  index: number;
}

interface IExitEditorAction {
  type: typeof WebsiteBuilderActionType.EXIT_EDITOR;
}

interface IUndoAction {
  type: typeof WebsiteBuilderActionType.UNDO;
}

interface IRedoAction {
  type: typeof WebsiteBuilderActionType.REDO;
}

export type TWebsiteBuilderAction =
  | IExitEditorAction
  | ISetDesignModeAction
  | ISetPreviewModeAction
  | ILoadThemesAction
  | ISelectThemeAction
  | ILoadGlobalStylesAction
  | IUpdateGlobalStylesAction
  | ISelectNodeAction
  | IUpdateSelectedNodeAction
  | IUndoAction
  | IRedoAction
  | IAddNodeAction
  | IDeleteNodeAction
  | IMoveElementAction
  | IResizeElementAction
  | IBringToFrontElementAction
  | IBringToBackElementAction
  | IUpdateElementSettingsAction
  | IUpdateElementStretchAction
  | IMoveSectionAction
  | IResizeSectionAction;

function getInverseTreeAction(action: TWebsiteBuilderAction): TWebsiteBuilderAction | null {
  switch (action.type) {
    case WebsiteBuilderActionType.ADD_NODE:
      return getAddNodeInverseAction(action);
    case WebsiteBuilderActionType.DELETE_NODE:
      return getDeleteNodeInverseAction(action);
    case WebsiteBuilderActionType.MOVE_ELEMENT:
      return getMoveElementInverseAction(action);
    case WebsiteBuilderActionType.RESIZE_ELEMENT:
      return getResizeElementInverseAction(action);
    case WebsiteBuilderActionType.BRING_TO_FRONT_ELEMENT:
      return getBringToFrontElementInverseAction(action);
    case WebsiteBuilderActionType.BRING_TO_BACK_ELEMENT:
      return getBringToBackElementInverseAction(action);
    case WebsiteBuilderActionType.UPDATE_ELEMENT_SETTINGS:
      return getUpdateElementSettingsInverseAction(action);
    case WebsiteBuilderActionType.UPDATE_ELEMENT_STRETCH:
      return getUpdateElementStretchInverseAction(action);
    case WebsiteBuilderActionType.MOVE_SECTION:
      return getMoveSectionInverseAction(action);
    case WebsiteBuilderActionType.RESIZE_SECTION:
      return getResizeSectionInverseAction(action);
    default:
      return null;
  }
}

export function applyUndoTreeChange(tree: IWebsiteBuilderRoot, action: TWebsiteBuilderAction): IWebsiteBuilderRoot {
  const inverseAction = getInverseTreeAction(action);

  if (inverseAction) {
    return applyTreeChange(tree, inverseAction);
  } else {
    return tree;
  }
}

export function applyTreeChange(tree: IWebsiteBuilderRoot, action: TWebsiteBuilderAction): IWebsiteBuilderRoot {
  switch (action.type) {
    case WebsiteBuilderActionType.ADD_NODE:
      return applyAddNodeTreeChange(tree, action);
    case WebsiteBuilderActionType.DELETE_NODE:
      return applyDeleteNodeTreeChange(tree, action);
    case WebsiteBuilderActionType.MOVE_ELEMENT:
      return applyMoveElementTreeChange(tree, action);
    case WebsiteBuilderActionType.RESIZE_ELEMENT:
      return applyResizeElementTreeChange(tree, action);
    case WebsiteBuilderActionType.BRING_TO_FRONT_ELEMENT:
      return applyBringToFrontElementTreeChange(tree, action);
    case WebsiteBuilderActionType.BRING_TO_BACK_ELEMENT:
      return applyBringToBackElementTreeChange(tree, action);
    case WebsiteBuilderActionType.UPDATE_ELEMENT_SETTINGS:
      return applyUpdateElementSettingsTreeChange(tree, action);
    case WebsiteBuilderActionType.UPDATE_ELEMENT_STRETCH:
      return applyUpdateElementStretchTreeChange(tree, action);
    case WebsiteBuilderActionType.MOVE_SECTION:
      return applyMoveSectionTreeChange(tree, action);
    case WebsiteBuilderActionType.RESIZE_SECTION:
      return applyResizeSectionTreeChange(tree, action);
    default:
      return tree;
  }
}
